import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Collapse,
  Chip,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
  Button,
  Menu,
  MenuItem,
  Avatar,
  Grid
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  MoreVert as MoreIcon
} from '@mui/icons-material';

const MobileDataCard = ({
  item,
  primaryText,
  secondaryText,
  avatar,
  chips = [],
  details = [],
  actions = [],
  onClick
}) => {
  const [expanded, setExpanded] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleActionClick = (action) => {
    handleMenuClose();
    action.onClick(item);
  };

  return (
    <Card sx={{ mb: 1, cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <CardContent sx={{ pb: 1 }}>
        {/* Header Row */}
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" flex={1}>
            {avatar && (
              <Avatar
                sx={{ mr: 2, width: 40, height: 40 }}
                src={avatar.src}
                alt={avatar.alt}
              >
                {avatar.fallback}
              </Avatar>
            )}
            <Box flex={1}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {primaryText}
              </Typography>
              {secondaryText && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {secondaryText}
                </Typography>
              )}
            </Box>
          </Box>

          <Box display="flex" alignItems="center">
            {actions.length > 0 && (
              <IconButton size="small" onClick={handleMenuOpen}>
                <MoreIcon />
              </IconButton>
            )}
            {details.length > 0 && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(!expanded);
                }}
              >
                {expanded ? <CollapseIcon /> : <ExpandIcon />}
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Chips Row */}
        {chips.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
            {chips.map((chip, index) => (
              <Chip
                key={index}
                label={chip.label}
                color={chip.color || 'default'}
                size="small"
                variant={chip.variant || 'filled'}
                icon={chip.icon}
              />
            ))}
          </Stack>
        )}

        {/* Expandable Details */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {details.map((detail, index) => (
                <Grid item xs={6} key={index}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {detail.label}
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {detail.value}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Collapse>
      </CardContent>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { minWidth: 150 }
        }}
      >
        {actions.map((action, index) => (
          <MenuItem
            key={index}
            onClick={() => handleActionClick(action)}
            disabled={action.disabled}
          >
            {action.icon && <Box sx={{ mr: 1, display: 'flex' }}>{action.icon}</Box>}
            {action.label}
          </MenuItem>
        ))}
      </Menu>
    </Card>
  );
};

const MobileDataTable = ({
  data = [],
  renderCard,
  loading = false,
  emptyMessage = "No data available",
  loadingMessage = "Loading...",
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!isMobile) {
    return null; // Only render on mobile
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <Typography variant="body2" color="text.secondary">
          {loadingMessage}
        </Typography>
      </Box>
    );
  }

  if (data.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Box {...props}>
      {data.map((item, index) => renderCard(item, index))}
    </Box>
  );
};

MobileDataTable.Card = MobileDataCard;

export default MobileDataTable;